import React, { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { format } from "date-fns";
import { Alert } from "types/Alert";
import { useAlerts } from "context/AlertsContext";
import { useNavigate } from "react-router-dom";
import { connectToWebSocket, toCamelCase } from "apiClient";
import { Dialog } from "./ui/Dialog";
import { getAlertBgClass } from "lib/alerts";
import { useStudents } from "context/StudentsContext";

type AlertProps = {
  teacherId: number;
};

export function Alerts({ teacherId }: AlertProps) {
  const { alerts, loading, setAlerts } = useAlerts();
  const { setSelectedStudentId } = useStudents();
  const [open, setOpen] = useState(false);

  const socketRef = useRef<ReturnType<typeof connectToWebSocket> | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    const socket = connectToWebSocket(
      `/ws/alerts/${teacherId}/`,
      (evt: MessageEvent) => {
        const newAlert = toCamelCase(JSON.parse(evt.data));
        setAlerts((prev) => {
          const alreadyExists = prev.some((alert) => alert.id === newAlert.id);
          return alreadyExists ? prev : [...prev, newAlert];
        });
      }
    );
    socketRef.current = socket;

    return () => {
      socket.close();
      socketRef.current = null;
    };
  }, [teacherId, setAlerts]);

  const buttonRef = React.useRef<HTMLButtonElement | null>(null);

  const unreadCount = alerts.length;

  const handleAlertClick = (alert: Alert) => {
    setSelectedStudentId(alert.studentId);
    setOpen(false);
    navigate("/");
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="w-8 h-8 lg:w-10 lg:h-10 bg-green-100 hover:bg-green-200 text-green-600 rounded-full flex items-center justify-center relative"
        title="Alerts"
        ref={buttonRef}
      >
        <Bell className="w-4 h-4 lg:w-5 lg:h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 lg:w-5 lg:h-5 text-xs font-bold text-white bg-red-600 rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      <Dialog
        open={open}
        triggerRef={buttonRef}
        onClose={() => setOpen(false)}
        className="w-80 lg:w-96"
        position="bottom-start"
      >
        <h3 className="text-sm lg:text-md font-semibold mb-2">Alerts</h3>
        <div className="max-h-80 overflow-y-auto space-y-2">
          {loading ? (
            <p className="text-xs lg:text-sm text-gray-500">Loading...</p>
          ) : alerts.length === 0 ? (
            <p className="text-xs lg:text-sm text-gray-500">No alerts</p>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-2 lg:p-3 rounded-lg cursor-pointer ${getAlertBgClass(
                  alert.role
                )}`}
                onClick={() => handleAlertClick(alert)}
              >
                <p className="text-xs lg:text-sm">{alert.content}</p>
                <p className="text-xs text-gray-500 mt-1 text-right">
                  {alert.sentAt && format(new Date(alert.sentAt), "HH:mm")}
                </p>
              </div>
            ))
          )}
        </div>
      </Dialog>
    </div>
  );
}
